/* eslint-disable no-useless-escape */

/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

type ValidationConfig = Record<string, string>;
type TranslationList = Record<string, string>;
type ErrorsList = Record<string, string>;
export type ValidationFieldsConfig = {
  validateRules: Record<string, string>[];
  code?: string;
  customUpperCode: string;
  required: boolean;
};

enum ConfigEnumLength {
  MIN_TEXT_LENGTH = 'MIN_TEXT_LENGTH',
  MAX_TEXT_LENGTH = 'MAX_TEXT_LENGTH',
  DATE_RANGE_MIN = 'DATE_RANGE_MIN',
  DATE_RANGE_MAX = 'DATE_RANGE_MAX',
}

export enum InputValidation {
  Numeric = 'numeric',
  AlphanumWithSpaces = 'alphanum-with-spaces',
  Alphanumeric = 'alphanumeric',
  Alpha = 'alpha',
  Email = 'email',
  Length = 'length',
  Date = 'date',
  Url = 'url',
}

const flattenObjectsArray = (arr: ValidationConfig[]): ValidationConfig => {
  return arr.reduce((acc, obj) => {
    return { ...acc, [obj.name]: obj.value };
  }, {});
};

//The basic material for the functions responsible for validation was taken from https://github.com/magento/magento2/blob/2.4/app/code/Magento/Ui/view/base/web/js/lib/validation/rules.js
export const validateNumeric = (value: string): boolean => /^\d+$/.test(value);

export const validateAlphanumWithSpaces = (value: string): boolean =>
  /^[a-zA-Z0-9\s]+$/.test(value);

export const validateAlphanumeric = (value: string): boolean =>
  /^[a-zA-Z0-9]+$/.test(value);

export const validateAlpha = (value: string): boolean =>
  /^[a-zA-Z]+$/.test(value);

export const validateEmail = (value: string): boolean =>
  /^[a-z0-9,!\#\$%&'\*\+\/=\?\^_`\{\|\}~-]+(\.[a-z0-9,!\#\$%&'\*\+\/=\?\^_`\{\|\}~-]+)*@([a-z0-9-]+\.)+[a-z]{2,}$/i.test(
    value
  );

export const validateDate = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(Date.parse(value));

export const isDateWithinRange = (
  date: string,
  minTimestamp?: number,
  maxTimestamp?: number
): boolean => {
  const dateTimestamp = new Date(date).getTime() / 1000;

  if (isNaN(dateTimestamp) || dateTimestamp < 0) {
    return false;
  }

  if (minTimestamp !== undefined && dateTimestamp < minTimestamp) {
    return false;
  }

  if (maxTimestamp !== undefined && dateTimestamp > maxTimestamp) {
    return false;
  }

  return true;
};

export const convertTimestampToDate = (
  timestamp: string | null | undefined
): string => {
  if (!timestamp || isNaN(Number(timestamp))) {
    return '';
  }

  return new Date(parseInt(timestamp, 10) * 1000).toISOString().split('T')[0];
};

export const validateUrl = (url: string) => {
  const urlPattern =
    /^(https?|ftp):\/\/(([A-Z0-9]([A-Z0-9_-]*[A-Z0-9]|))(\.[A-Z0-9]([A-Z0-9_-]*[A-Z0-9]|))*)(:(\d+))?(\/[A-Z0-9~](([A-Z0-9_~-]|\.)*[A-Z0-9~]|))*\/?(.*)?$/i;
  return urlPattern.test(url);
};

export const validateLength = (
  value: string,
  minLength: number,
  maxLength: number
): boolean => {
  const length = value.length;
  return length >= minLength && length <= maxLength;
};

export const validationFields = (
  value: string,
  configs: ValidationFieldsConfig,
  translations: TranslationList,
  errorsList: ErrorsList
) => {
  const {
    requiredFieldError,
    lengthTextError,
    numericError,
    alphaNumWithSpacesError,
    alphaNumericError,
    alphaError,
    emailError,
    dateError,
    urlError,
    dateLengthError,
  } = translations;

  const fieldName = configs?.customUpperCode as string;

  const defaultFields = { [fieldName]: '' };

  if (errorsList[fieldName]) {
    delete errorsList[fieldName];
  }

  if (configs?.required && (!value || value === 'false')) {
    return { [fieldName]: requiredFieldError };
  }

  if (!configs?.required && !value) {
    return defaultFields;
  }

  if (!configs?.validateRules?.length) return defaultFields;

  const validateRulesConfig = flattenObjectsArray(configs?.validateRules);

  const min = validateRulesConfig[ConfigEnumLength.MIN_TEXT_LENGTH] ?? 1;
  const max = validateRulesConfig[ConfigEnumLength.MAX_TEXT_LENGTH] ?? 255;
  const dateMin = validateRulesConfig[ConfigEnumLength.DATE_RANGE_MIN];
  const dateMax = validateRulesConfig[ConfigEnumLength.DATE_RANGE_MAX];

  if (!validateLength(value, +min, +max) && !(dateMin || dateMax)) {
    return {
      [fieldName]: lengthTextError.replace('{min}', min).replace('{max}', max),
    };
  }

  if (!isDateWithinRange(value, +dateMin, +dateMax) && (dateMin || dateMax)) {
    return {
      [fieldName]: dateLengthError
        .replace('{min}', convertTimestampToDate(dateMin))
        .replace('{max}', convertTimestampToDate(dateMax)),
    };
  }

  const validationMap = {
    [InputValidation.Numeric]: {
      validate: validateNumeric,
      error: numericError,
    },
    [InputValidation.AlphanumWithSpaces]: {
      validate: validateAlphanumWithSpaces,
      error: alphaNumWithSpacesError,
    },
    [InputValidation.Alphanumeric]: {
      validate: validateAlphanumeric,
      error: alphaNumericError,
    },
    [InputValidation.Alpha]: {
      validate: validateAlpha,
      error: alphaError,
    },
    [InputValidation.Email]: {
      validate: validateEmail,
      error: emailError,
    },
    [InputValidation.Date]: {
      validate: validateDate,
      error: dateError,
    },
    [InputValidation.Url]: {
      validate: validateUrl,
      error: urlError,
    },
  };

  const validation =
    validationMap[
      validateRulesConfig['INPUT_VALIDATION'] as keyof typeof validationMap
    ];

  if (
    validation &&
    !validation.validate(value) &&
    !errorsList[fieldName]?.length
  ) {
    return { [fieldName]: validation.error };
  }

  return defaultFields;
};
